import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { APP_SECTION_LIST } from 'src/constants/queryKeys';
import { networkRemoveAppSection, useFetchAppSectionList } from 'src/network/app-section';
import useFormsModalStore from 'src/store/forms';
import { useLocalStorage } from '../useLocalStorage';
import { getComparator } from '../useTable';

function applySortFilter({ tableData, comparator, filterName }) {
  const stabilizedThis = tableData.map((el, index) => [el, index]);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  tableData = stabilizedThis.map((el) => el[0]);

  if (filterName) {
    tableData = tableData.filter((item) => item.ApplicationSectionLable.toLowerCase().indexOf(filterName.toLowerCase()) !== -1);
  }

  return tableData;
}

export const useAppSectionList = (params) => {
  const { page, setPage, projectId, selected, setSelected, order, orderBy } = params;

  const queryClient = useQueryClient();

  const { enqueueSnackbar } = useSnackbar();
  const [, setSavedPage] = useLocalStorage('savedPage');

  const { setModal } = useFormsModalStore();

  const [tableData, setTableData] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const pageIndex = Number(searchParams.get('page')) || 0;

  const [filterName, setFilterName] = useState('');

  // ---------------------------------------------------------------------------------

  const handleDeleteRow = (appSectionId) => {
    setDeleteLoading(true);
    networkRemoveAppSection(appSectionId)
      .then((res) => {
        const newTableData = tableData.filter((row) => row.ApplicationSectionID !== appSectionId);
        setTableData(newTableData);
        enqueueSnackbar('بخش موردنظر با موفقیت حذف شد');

        const newSelectedItems = selected?.filter((id) => id !== appSectionId);
        setSelected(newSelectedItems);

        queryClient.invalidateQueries({ queryKey: [APP_SECTION_LIST, projectId] });
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setOpenConfirmDelete(false);
        setDeleteLoading(false);
      });
  };

  const handleDeleteRows = async (appSectionIDs) => {
    try {
      setDeleteLoading(true);

      const deletePromises = appSectionIDs.map((id) => networkRemoveAppSection(id));

      await Promise.all(deletePromises);

      const newTableData = tableData.filter((row) => !appSectionIDs.includes(row.ApplicationSectionID));
      setTableData(newTableData);

      enqueueSnackbar('بخش های موردنظر با موفقیت حذف شدند');

      await queryClient.invalidateQueries({ queryKey: [APP_SECTION_LIST, projectId] });

      setSelected([]);
    } catch (error) {
      console.log(error);
    } finally {
      setOpenConfirmDelete(false);
      setDeleteLoading(false);
    }
  };

  const handleEditRow = (appSection) => setModal({ data: appSection, type: APP_SECTION_LIST, isOpen: true, title: 'ویرایش بخش' });

  const handleFilterName = (filterName) => setFilterName(filterName);

  const { isLoading, isFetching, data } = useFetchAppSectionList(page, 50, projectId);

  useEffect(() => {
    if (data) {
      const appSecions = data?.data?.items || [];
      setTableData([...appSecions]);
      setSavedPage({
        pageIndex: page,
        pageName: 'app-sections',
      });
    }
  }, [data, page]);

  const dataFiltered = applySortFilter({
    tableData: tableData || [],
    comparator: getComparator(order, orderBy),
    filterName,
  });

  useEffect(() => {
    setPage(pageIndex);
  }, [pageIndex]);

  return {
    tableData: dataFiltered,
    isLoading: isLoading || isFetching,
    isFetching,
    deleteLoading,
    handleDeleteRow,
    handleDeleteRows,
    handleEditRow,
    filterName,
    handleFilterName,
    openConfirmDelete,
    setOpenConfirmDelete,
  };
};
